import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Avatar,
  SimpleGrid,
  useBreakpointValue
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * Course Testimonials Section
 * Student reviews and success stories
 */
const CourseTestimonials = ({ data }) => {
  const gridCols = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  if (!data) return null;

  const {
    title = "What Students Say",
    subtitle,
    testimonials = []
  } = data;

  if (testimonials.length === 0) return null;

  return (
    <Box py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="container.lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          mb={{ base: 12, md: 16 }}
        >
          <VStack align="center" spacing={3} textAlign="center">
            <Heading as="h2" size="2xl" fontWeight="900" color="gray.900">
              {title}
            </Heading>
            {subtitle && (
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                {subtitle}
              </Text>
            )}
          </VStack>
        </MotionBox>

        {/* Testimonials Grid */}
        <SimpleGrid columns={gridCols} spacing={{ base: 6, md: 8 }}>
          {testimonials.map((testimonial, idx) => (
            <MotionBox
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1, duration: 0.6 }}
            >
              <Box
                bg="gray.50"
                p={6}
                borderRadius="lg"
                h="full"
                display="flex"
                flexDirection="column"
              >
                {/* Rating */}
                {testimonial.rating && (
                  <Box mb={4}>
                    <Text fontSize="lg">
                      {'⭐'.repeat(testimonial.rating)}
                    </Text>
                  </Box>
                )}

                {/* Quote */}
                <Text
                  fontSize="sm"
                  color="gray.700"
                  mb={6}
                  flex={1}
                  lineHeight="1.6"
                  fontStyle="italic"
                >
                  "{testimonial.quote}"
                </Text>

                {/* Author */}
                <HStack spacing={3} mt="auto">
                  {testimonial.avatar && (
                    <Avatar
                      size="sm"
                      name={testimonial.name}
                      src={testimonial.avatar}
                    />
                  )}
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="600" color="gray.900">
                      {testimonial.name}
                    </Text>
                    {testimonial.title && (
                      <Text fontSize="xs" color="gray.600">
                        {testimonial.title}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </Box>
            </MotionBox>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default CourseTestimonials;
